import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'antd';
import http, { setTokens, clearTokens } from '../api/http';
import { authLogout, authPing } from '../api/auth';
import { startIssuesRealtime, stopIssuesRealtime } from '../realtime/issues';
import { startSortiesRealtime, stopSortiesRealtime } from '../realtime/sorties';
import { startReportsRealtime, stopReportsRealtime } from '../realtime/reports';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem('user');
    return raw ? JSON.parse(raw) : null;
  });
  const [loading, setLoading] = useState(false);

  const pingTimerRef = useRef(null);

  const login = async (email, password) => {
    setLoading(true);
    try {
      const { data } = await http.post('/auth/login', { email, password });
      setTokens({ access: data.access, refresh: data.refresh });
      setUser(data.user);
      localStorage.setItem('user', JSON.stringify(data.user));
      Modal.success({ title: 'Connexion réussie' });
      return { ok: true, user: data.user };
    } catch (e) {
      const msg = e?.response?.data?.error || 'Identifiants invalides';
      Modal.error({ title: 'Erreur de connexion', content: msg });
      return { ok: false, error: msg };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
    } catch {}

    try {
      await authLogout();
    } catch {}
    clearTokens();
    setUser(null);
    localStorage.removeItem('user');
    try {
      stopIssuesRealtime();
      stopSortiesRealtime();
      stopReportsRealtime();
    } catch {}
  };

  useEffect(() => {
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }

    if (!user) return;

    try {
      startIssuesRealtime();
      startSortiesRealtime();
      startReportsRealtime();
    } catch {}

    const doPing = async () => {
      try {
        await authPing();
      } catch {
        // ignore heartbeat failures
      }
    };

    // immediate ping and then periodic
    doPing();
    pingTimerRef.current = setInterval(doPing, 60_000);

    return () => {
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }

      try {
        stopIssuesRealtime();
        stopSortiesRealtime();
        stopReportsRealtime();
      } catch {}
    };
  }, [user]);

  const value = useMemo(() => ({ user, setUser, login, logout, loading }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
